import { DatePipe, LowerCasePipe } from '@angular/common';
import { Component, computed, effect } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChatbotComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot/chatbot.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { LineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.component';
import { ILineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { IChatBotAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EmbeddedVideoPlayerComponent } from '@shared/components/embedded-video-player/embedded-video-player.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { StreamDetailsDeliverableBaseDirective } from '../../stream-details-deliverable-base.directive';
import { IChatbotStreamAnalyticsTableData } from './chatbot-stream-analytics.interface';

@Component({
  imports: [
    DatePipe,
    TranslateModule,
    LineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    EmbeddedVideoPlayerComponent,
    IconBadgeComponent,
    ChatbotComponent,
    TextClampTooltipDirective,
    MatTooltipModule,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-chatbot-stream-analytics',
  styleUrls: ['./chatbot-stream-analytics.component.scss'],
  templateUrl: './chatbot-stream-analytics.component.html',
})
export class ChatbotStreamAnalyticsComponent extends StreamDetailsDeliverableBaseDirective<
  IChatbotStreamAnalyticsTableData,
  IChatBotAnalytics
> {
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'chatbot-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.chatBot.totalCount'),
    value: 0,
  };
  public override readonly displayedColumns: string[] = ['timestamp', 'videoTime', 'user', 'title', 'actions'];
  public override readonly dataSource: MatTableDataSource<IChatbotStreamAnalyticsTableData> = new MatTableDataSource();
  public override readonly deliverableType = EDeliverableType.chatBot;
  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.chatbot?.find((cb) => cb.deliverableId === deliverableId);
    }

    return null;
  });

  constructor() {
    super();

    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setStatus();
        this.setStreamData();
      }
    });

    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings()) {
        this.setCounts();

        if (this.timelineData()) {
          this.prepareChartAndTableData();
        }
      }
    });

    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        const chatbot = this.currentDeliverable();

        this.keywords = chatbot?.text ? [chatbot.text] : [];
      }
    });
  }

  protected override prepareChartAndTableData(): void {
    const tableData: IChatbotStreamAnalyticsTableData[] = [];
    const dotGraphData: ILineGraphInputData[] = [];

    const currentChatbotAnalytics = this.currentDeliverable();

    const timelineData = this.timelineData() || [];
    const { sortedDictionary } = this.graphUtilities.processTimelineData(
      timelineData,
      this.stream()?.executionTime,
      this.stream()?.end,
    );

    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();

    currentChatbotAnalytics?.findings
      ?.filter((f) => !removedIds.has(f.id))
      .forEach((finding) => {
        const { id } = finding;
        const chatTimestamp = this.dateUtils.parseDateTimeFromApi(finding.timestamp).getTime();

        dotGraphData.push({
          id,
          status: finding.status,
          xAxisValue: chatTimestamp,
          yAxisValue: Math.round(this.graphUtilities.getInterpolatedViewCount(sortedDictionary, chatTimestamp)),
        });

        tableData.push({
          findingParentId: finding.parentId,
          id,
          text: finding.text,
          timestamp: finding.timestamp,
          user: finding.user,
          videoTimeLabel: AnalyticsDetailsUtilitiesService.formatVideoTime(finding.videoTime),
          videoTimeSeconds: finding.videoTime ?? 0,
        });
      });

    this.dataSource.data = tableData;
    this.dataCount.set(tableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
  }

  protected override setCounts(): void {
    const removedIds = this.manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();
    const activeFindings = this.currentDeliverable()?.findings?.filter((f) => !removedIds.has(f.id)) ?? [];

    this.kpiCardData.value = activeFindings.length;
  }

  protected override setKeywordList(): void {
    return;
  }
}
